import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface RevenueData {
  ticketsSold: number;
  revenue: number;
  ticketCapacity: number;
  ticketsAvailable: number;
}

interface TierSoldEntry {
  tierId: string;
  tierName: string;
  tierPrice: number;
  count: number;
  compCount?: number;
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f2d1f',
    margin: 0,
  },
  badge: {
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '0.2rem 0.6rem',
    borderRadius: '12px',
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  progressContainer: {
    marginBottom: '1rem',
  },
  progressBar: {
    width: '100%',
    height: '12px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    overflow: 'hidden',
    position: 'relative' as const,
  },
  progressFill: {
    height: '100%',
    borderRadius: '6px',
    background: 'linear-gradient(90deg, #1a6b3c, #2a8f54)',
    transition: 'width 0.6s ease',
  },
  statsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '0.5rem',
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  tierBreakdown: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #f3f4f6',
  },
  tierRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.4rem 0',
    fontSize: '0.85rem',
  },
  tierName: {
    color: '#374151',
    fontWeight: 500,
  },
  tierCount: {
    color: '#6b7280',
    fontSize: '0.8rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '1.5rem',
    color: '#6b7280',
    fontSize: '0.85rem',
  },
  error: {
    textAlign: 'center' as const,
    padding: '1rem',
    color: '#6b7280',
    fontSize: '0.85rem',
  },
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// Tier colors for the stacked progress bar
const TIER_COLORS = ['#1a6b3c', '#c9a84c', '#2563eb', '#9333ea', '#dc2626', '#0891b2'];

export function TicketSalesProgressCard() {
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [tierData, setTierData] = useState<TierSoldEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [tiers, capacityResult] = await Promise.all([
          apiClient<TierSoldEntry[]>('/legacy-ball/tier-tickets-sold'),
          apiClient<{ capacity: number }>('/legacy-ball/ticket-capacity').catch(() => null),
        ]);
        const totalSold = tiers.reduce((sum, t) => sum + t.count, 0);
        const totalRevenue = tiers.reduce((sum, t) => sum + (t.count - (t.compCount || 0)) * t.tierPrice, 0);
        const capacity = capacityResult?.capacity ?? 0;
        setRevenueData({
          ticketsSold: totalSold,
          revenue: totalRevenue,
          ticketCapacity: capacity,
          ticketsAvailable: Math.max(0, capacity - totalSold),
        });
        setTierData(tiers);
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={styles.card}>
        <div style={styles.loading}>Loading ticket sales...</div>
      </div>
    );
  }

  if (!revenueData) {
    return (
      <div style={styles.card}>
        <div style={styles.error}>Unable to load ticket sales data</div>
      </div>
    );
  }

  const { ticketsSold, revenue, ticketCapacity, ticketsAvailable } = revenueData;
  const percentage = ticketCapacity > 0 ? Math.min(Math.round((ticketsSold / ticketCapacity) * 100), 100) : 0;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h3 style={styles.title}>🎫 Ticket Sales</h3>
        <span style={styles.badge}>{percentage}% sold</span>
      </div>

      <div style={styles.progressContainer}>
        <div style={styles.progressBar}>
          {tierData.map((tier, index) => {
            const tierPercent = ticketCapacity > 0 ? (tier.count / ticketCapacity) * 100 : 0;
            return (
              <div
                key={tier.tierId}
                style={{
                  height: '100%',
                  width: `${tierPercent}%`,
                  backgroundColor: TIER_COLORS[index % TIER_COLORS.length],
                  display: 'inline-block',
                  transition: 'width 0.6s ease',
                }}
                title={`${tier.tierName}: ${tier.count} sold`}
              />
            );
          })}
        </div>
        <div style={styles.statsRow}>
          <span>{ticketsSold} sold</span>
          <span>{ticketsAvailable} remaining</span>
          <span>{formatCurrency(revenue)} revenue</span>
        </div>
      </div>

      {tierData.length > 0 && (
        <div style={styles.tierBreakdown}>
          {tierData.map((tier, index) => (
            <div key={tier.tierId} style={styles.tierRow}>
              <span style={styles.tierName}>
                <span style={{
                  display: 'inline-block',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: TIER_COLORS[index % TIER_COLORS.length],
                  marginRight: '8px',
                }} />
                {tier.tierName}
              </span>
              <span style={styles.tierCount}>
                {tier.count} sold · {formatCurrency(tier.count * tier.tierPrice)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
